import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../store/authStore'
import { apiClient } from '../api/client'
import { API_ENDPOINTS } from '../api/endpoints'
import { getMenuNameFromPath } from '../utils/menuPathMapper'
import { useCallback, useMemo } from 'react'

interface Menu {
  menuId: number
  menuName: string
  menuText: string
  menuURL?: string
  parentMenuId?: number
  menuLevel?: number
  menuOrder?: number
}

interface UserMenu {
  userMenuId?: number  // camelCase
  UserMenuId?: number  // PascalCase (from SQL/API)
  userId?: number
  UserId?: number  // PascalCase
  menuId?: number
  MenuId?: number  // PascalCase (from JOIN)
  permissionLevel?: number
  PermissionLevel?: number  // PascalCase
  active?: number
  Active?: number  // PascalCase
  menuName?: string
  MenuName?: string  // From JOIN
  menuText?: string
  MenuText?: string  // From JOIN
  menuURL?: string
  MenuURL?: string  // From JOIN
}

interface RoleMenu {
  roleMenuId?: number  // camelCase
  RoleMenuId?: number  // PascalCase (from SQL/API)
  roleId?: number
  RoleId?: number  // PascalCase
  menuId?: number
  MenuId?: number  // PascalCase (from JOIN)
  permissionLevel?: number
  PermissionLevel?: number  // PascalCase
  permissionLevelStr?: string
  PermissionLevelStr?: string  // PascalCase
  active?: number
  Active?: number  // PascalCase
  roleName?: string
  RoleName?: string  // From JOIN
  menuName?: string
  MenuName?: string  // From JOIN
  menuText?: string
  MenuText?: string  // From JOIN
  menuURL?: string
  MenuURL?: string  // From JOIN
}

interface ActionPermission {
  actionPermissionId: number
  menuId: number
  userId?: number | null
  roleId?: number | null
  actionType: string
  allowed: boolean | number  // Can be boolean or number (1/0)
  active: number | boolean  // Can be number (1/0) or boolean
}

// Helper function to check if active is true (supports both boolean and number)
const isActive = (active: number | boolean | undefined): boolean => {
  if (active === undefined) return false
  if (typeof active === 'boolean') return active === true
  if (typeof active === 'number') return active === 1
  return false
}

// Helper function to check if allowed is true (supports both boolean and number)
const isAllowed = (allowed: boolean | number | undefined): boolean => {
  if (allowed === undefined) return false
  if (typeof allowed === 'boolean') return allowed === true
  if (typeof allowed === 'number') return allowed === 1
  return false
}

export function usePermissions() {
  const { user } = useAuthStore()
  
  // Get user role and isAdmin status with improved logic
  const { userRole, isAdmin } = useMemo(() => {
    if (!user) return { userRole: null, isAdmin: false }
    
    const role = user.role
    const roles = user.roles
    
    let primaryRole = role || (Array.isArray(roles) && roles.length > 0 ? roles[0] : null)
    
    // Check if roles is a string (comma separated)
    let rolesArray: string[] = []
    if (Array.isArray(roles)) {
      rolesArray = roles
    } else if (typeof roles === 'string') {
      rolesArray = (roles as string).split(',').map(r => r.trim())
    }
    
    const isAdminRole = (value: unknown) =>
      String(value || '').trim().toLowerCase() === 'admin'

    const adminStatus =
      isAdminRole(primaryRole) || rolesArray.some((r) => isAdminRole(r))
    
    return { userRole: primaryRole, isAdmin: adminStatus }
  }, [user])

  // Get all menus
  const { data: menus, isLoading: loadingMenus, error: menusError } = useQuery<Menu[]>({
    queryKey: ['menus'],
    queryFn: async () => {
      try {
        const response = await apiClient.get(API_ENDPOINTS.menus.list)
        const data = response.data
        if (Array.isArray(data)) return data
        if (data?.data && Array.isArray(data.data)) return data.data
        if (data?.menus && Array.isArray(data.menus)) return data.menus
        return []
      } catch (error: any) {
        console.error('[Permissions] Error loading menus:', error)
        return []
      }
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  })

  // Get user-specific menus
  const { data: userMenus, isLoading: loadingUserMenus } = useQuery<UserMenu[]>({
    queryKey: ['userMenus', user?.id],
    queryFn: async () => {
      if (!user) return []
      try {
        const userId = (user as any).userId || (user as any).id || user.username
        const params: any = {}
        if (userId) params.userId = userId
        
        const response = await apiClient.get(API_ENDPOINTS.userMenus.list, { params })
        const data = response.data
        if (Array.isArray(data)) return data
        if (data?.data && Array.isArray(data.data)) return data.data
        return []
      } catch (error: any) {
        if (error.response?.status === 403 || error.response?.status === 401) return []
        throw error
      }
    },
    enabled: !!user,
    retry: false,
  })

  // Get role menus for the user's role
  const { data: roleMenus, isLoading: loadingRoleMenus } = useQuery<RoleMenu[]>({
    queryKey: ['roleMenus', userRole],
    queryFn: async () => {
      if (!userRole) return []
      try {
        const rolesResponse = await apiClient.get(API_ENDPOINTS.roles.list)
        let roles: Array<{ roleId: number; roleName: string }> = []
        
        const rolesData = rolesResponse.data
        if (Array.isArray(rolesData)) roles = rolesData
        else if (rolesData?.data && Array.isArray(rolesData.data)) roles = rolesData.data
        else if (rolesData?.roles && Array.isArray(rolesData.roles)) roles = rolesData.roles

        const matchedRole = roles.find(r => r.roleName === userRole)
        if (!matchedRole) return []
        
        const response = await apiClient.get(API_ENDPOINTS.roleMenus.list, {
          params: { roleId: matchedRole.roleId },
        })
        const data = response.data
        if (Array.isArray(data)) return data
        if (data?.data && Array.isArray(data.data)) return data.data
        return []
      } catch (error) {
        console.error('[Permissions] Error loading role menus:', error)
        return []
      }
    },
    enabled: !!userRole,
  })

  // Get user action permissions
  const { data: userActionPermissions, isLoading: loadingUserActionPermissions } = useQuery<ActionPermission[]>({
    queryKey: ['userActionPermissions', user?.id],
    queryFn: async () => {
      if (!user) return []
      try {
        const userId = (user as any).userId || (user as any).id || user.username
        if (!userId) return []
        
        const response = await apiClient.get(API_ENDPOINTS.actionPermissions.getUserPermissions(String(userId)))
        const data = response.data
        if (Array.isArray(data)) return data
        if (data?.data && Array.isArray(data.data)) return data.data
        return []
      } catch (error: any) {
        return []
      }
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  })

  // Get role action permissions
  const { data: roleActionPermissions, isLoading: loadingRoleActionPermissions } = useQuery<ActionPermission[]>({
    queryKey: ['roleActionPermissions', userRole],
    queryFn: async () => {
      if (!userRole) return []
      try {
        const rolesResponse = await apiClient.get(API_ENDPOINTS.roles.list)
        let roles: Array<{ roleId: number; roleName: string }> = []
        const rolesData = rolesResponse.data
        if (Array.isArray(rolesData)) roles = rolesData
        else if (rolesData?.data && Array.isArray(rolesData.data)) roles = rolesData.data
        
        const matchedRole = roles.find(r => r.roleName === userRole)
        if (!matchedRole) return []
        
        const response = await apiClient.get(API_ENDPOINTS.actionPermissions.getRolePermissions(String(matchedRole.roleId)))
        const data = response.data
        if (Array.isArray(data)) return data
        if (data?.data && Array.isArray(data.data)) return data.data
        return []
      } catch (error: any) {
        return []
      }
    },
    enabled: !!userRole,
    staleTime: 5 * 60 * 1000,
  })

  // Helper function to normalize paths for comparison
  const normalizePath = (path: string): string => {
    if (!path) return ''
    let p = path.startsWith('/') ? path : `/${path}`
    // Remove trailing slash if not just "/"
    if (p.length > 1 && p.endsWith('/')) {
      p = p.slice(0, -1)
    }
    return p
  }

  // Helper function to check if two paths match
  const pathsMatch = (path1: string, path2: string): boolean => {
    if (!path1 || !path2) return false
    const norm1 = normalizePath(path1)
    const norm2 = normalizePath(path2)
    return norm1 === norm2
  }

  // Get system settings for menu visibility
  const { data: menuSettings } = useQuery({
    queryKey: ['systemSettings', 'MenuVisibility'],
    queryFn: async () => {
      try {
        const response = await apiClient.get(`${API_ENDPOINTS.systemSettings.base}/MenuVisibility`)
        return response.data
      } catch (error) {
        return null
      }
    },
    staleTime: 5 * 60 * 1000,
  })

  // Check if user has permission to access a menu/path
  const hasPermission = useCallback((path: string): boolean => {
    if (!user) return false
    if (!path) return false
    
    // Step 0: Check global menu visibility settings from SignalSystemManagement/SystemSettings
    if (menuSettings && menuSettings.settingValue) {
      try {
        const hiddenMenus = JSON.parse(menuSettings.settingValue)
        if (Array.isArray(hiddenMenus)) {
          const normalizedPath = normalizePath(path)
          const isHidden = hiddenMenus.some(hiddenPath => {
            const normHidden = normalizePath(hiddenPath)
            return normalizedPath === normHidden || normalizedPath.startsWith(normHidden + '/')
          })
          if (isHidden) return false
        }
      } catch (e) {
        console.error('[Permissions] Error parsing MenuVisibility settings:', e)
      }
    }

    const normalizedPath = normalizePath(path)

    // Version / changelog page is available to every signed-in user
    if (normalizedPath === '/settings/about') {
      return true
    }

    // Security admin pages: never block via MenuVisibility for Admins
    const isSecurityAdminPath =
      normalizedPath === '/permissions' ||
      normalizedPath.startsWith('/permissions/') ||
      normalizedPath === '/roles' ||
      normalizedPath === '/users'

    // Admin bypass must run before the menus-loading gate; otherwise a menus refetch
    // returns false and ProtectedRoute/Sidebar incorrectly block every page.
    if (isAdmin) {
      if (!isSecurityAdminPath && menuSettings && menuSettings.settingValue) {
        try {
          const hiddenMenus = JSON.parse(menuSettings.settingValue)
          if (Array.isArray(hiddenMenus)) {
            const isHidden = hiddenMenus.some((hiddenPath: string) => {
              const normHidden = normalizePath(hiddenPath)
              return normalizedPath === normHidden || normalizedPath.startsWith(normHidden + '/')
            })
            if (isHidden) return false
          }
        } catch {
          // ignore parse errors
        }
      }
      // Still honor explicit user-level blocks below once menus are available
    }

    // Wait for menus to be loaded before checking permissions
    if (loadingMenus || menus === undefined) {
      return isAdmin
    }

    // Ensure menus is an array
    const menusArray = Array.isArray(menus) ? menus : []

    // Prefer exact path match so child menus (e.g. /permissions/actions) do not
    // steal ACL checks for the parent (/permissions).
    let menu = menusArray.find(m => {
      if (!m.menuURL) return false
      const menuPath = normalizePath(m.menuURL)
      return menuPath === normalizedPath || menuPath.toLowerCase() === normalizedPath.toLowerCase()
    })

    if (!menu) {
      menu = menusArray.find(m => {
        if (!m.menuURL) return false
        const menuPath = normalizePath(m.menuURL)
        return normalizedPath.startsWith(menuPath + '/')
      })
    }

    if (!menu) {
      const menuName = getMenuNameFromPath(normalizedPath)
      if (menuName) {
        menu = menusArray.find(m => m.menuName === menuName || m.menuText === menuName)
      }
    }

    // New fuel pages (daily prices, …) may exist in the UI before MAS_MENU is seeded.
    // Inherit ACL from any other fuel-station row so ProtectedRoute does not bounce to dashboard.
    if (!menu && (normalizedPath === '/fuel-meter' || normalizedPath.startsWith('/fuel-station/'))) {
      menu = menusArray.find(m => {
        if (!m.menuURL) return false
        const menuPath = normalizePath(m.menuURL)
        return menuPath === '/fuel-meter' || menuPath.startsWith('/fuel-station/')
      })
    }

    // Step 1: Check for explicit USER-LEVEL block (highest priority, even for Admins)
    if (menu && userMenus !== undefined) {
      const userMenu = userMenus.find(um => {
        const umMenuId = um.MenuId || um.menuId
        return umMenuId === menu!.menuId
      })

      if (userMenu) {
        const umActive = userMenu.Active !== undefined ? userMenu.Active : userMenu.active
        const permissionLevel = userMenu.PermissionLevel !== undefined ? userMenu.PermissionLevel : userMenu.permissionLevel
        
        // If explicitly inactive or level is 0, deny immediately (CRITICAL OVERRIDE)
        if (umActive === 0 || permissionLevel === 0) {
          return false
        }
        // If explicitly active and has level, allow immediately
        if (umActive === 1 && permissionLevel !== undefined && permissionLevel > 0) {
          return true
        }
      }
    }

    // Step 2: Admin has all permissions IF NOT explicitly blocked above
    // If we're checking a menu path, verify if it's hidden by SignalSystemManagement/MenuVisibility
    if (isAdmin) {
      const isSecurityAdminPath =
        normalizedPath === '/permissions' ||
        normalizedPath.startsWith('/permissions/') ||
        normalizedPath === '/roles' ||
        normalizedPath === '/users'
      if (!isSecurityAdminPath && menuSettings && menuSettings.settingValue) {
        try {
          const hiddenMenus = JSON.parse(menuSettings.settingValue)
          if (Array.isArray(hiddenMenus)) {
            const isHidden = hiddenMenus.some(hiddenPath => pathsMatch(path, hiddenPath))
            if (isHidden) return false
          }
        } catch (e) {}
      }
      return true
    }

    // Step 3: Check Role permissions
    if (menu && roleMenus !== undefined) {
      const roleMenu = roleMenus.find(rm => {
        const rmMenuId = rm.MenuId || rm.menuId
        const rmActive = rm.Active !== undefined ? rm.Active : rm.active
        return rmMenuId === menu!.menuId && rmActive === 1
      })
      if (roleMenu) {
        const permissionLevel = roleMenu.PermissionLevel !== undefined ? roleMenu.PermissionLevel : roleMenu.permissionLevel
        // Legacy rows may have Active=1 with null PermissionLevel — treat as granted
        if (permissionLevel === undefined || permissionLevel === null || permissionLevel > 0) {
          return true
        }
      }
    }

    // If we have a menu but no explicit permission found, and not an admin, deny
    if (menu) return false

    // Special case: if it's a known protected path but not in DB yet, 
    // allow only for admins, deny for others
    return isAdmin
  }, [user, menus, loadingMenus, userMenus, roleMenus, isAdmin, menuSettings])

  // Get permission level for a menu
  const getPermissionLevel = useCallback((path: string): number => {
    if (!user) return 0
    if (!path) return 0

    // Step 0: Check global menu visibility settings
    if (menuSettings && menuSettings.settingValue) {
      try {
        const hiddenMenus = JSON.parse(menuSettings.settingValue)
        if (Array.isArray(hiddenMenus)) {
          const normalizedPath = normalizePath(path)
          const isHidden = hiddenMenus.some(hiddenPath => {
            const normHidden = normalizePath(hiddenPath)
            return normalizedPath === normHidden || normalizedPath.startsWith(normHidden + '/')
          })
          if (isHidden) return 0
        }
      } catch (e) {}
    }

    if (loadingMenus || menus === undefined) {
      return 0
    }

    // Normalize path
    const normalizedPath = normalizePath(path)
    const menusArray = Array.isArray(menus) ? menus : []

    // Find menu by path
    let menu = menusArray.find(m => {
      if (!m.menuURL) return false
      const menuPath = normalizePath(m.menuURL)
      return menuPath === normalizedPath || menuPath.toLowerCase() === normalizedPath.toLowerCase()
    })

    if (!menu) {
      const menuName = getMenuNameFromPath(normalizedPath)
      if (menuName) {
        menu = menusArray.find(m => m.menuName === menuName || m.menuText === menuName)
      }
    }

    if (menu) {
      // Check user-specific permissions first (STRICT OVERRIDE)
      if (userMenus !== undefined) {
        const userMenu = userMenus.find(um => {
          const umMenuId = um.MenuId || um.menuId
          return umMenuId === menu!.menuId
        })
        if (userMenu) {
          const umActive = userMenu.Active !== undefined ? userMenu.Active : userMenu.active
          const permissionLevel = userMenu.PermissionLevel !== undefined 
            ? userMenu.PermissionLevel 
            : userMenu.permissionLevel
          
          if (umActive === 0) return 0
          if (permissionLevel !== undefined) return permissionLevel
        }
      }

      // Check role permissions
      if (roleMenus !== undefined) {
        const roleMenu = roleMenus.find(rm => {
          const rmMenuId = rm.MenuId || rm.menuId
          const rmActive = rm.Active !== undefined ? rm.Active : rm.active
          return rmMenuId === menu!.menuId && rmActive === 1
        })
        if (roleMenu) {
          const permissionLevel = roleMenu.PermissionLevel !== undefined 
            ? roleMenu.PermissionLevel 
            : roleMenu.permissionLevel
          if (permissionLevel) {
            return permissionLevel
          }
        }
      }
    }

    // Default for admins if not explicitly blocked
    if (isAdmin) return 999
    
    return 0
  }, [user, menus, loadingMenus, userMenus, roleMenus, isAdmin, menuSettings])

  // Check if user can perform an action (read, write, delete, etc.)
  const canPerformAction = useCallback((path: string, action: 'read' | 'write' | 'delete' | 'admin'): boolean => {
    const level = getPermissionLevel(path)
    
    switch (action) {
      case 'read':
        return level >= 1
      case 'write':
        return level >= 2
      case 'delete':
        return level >= 3
      case 'admin':
        return level >= 4
      default:
        return false
    }
  }, [getPermissionLevel])

  // Check detailed action permission
  const canPerformDetailedAction = useCallback(async (path: string, actionType: string): Promise<boolean> => {
    if (!user) return false
    if (!path) return false

    // Step 0: Check global menu visibility settings
    if (menuSettings && menuSettings.settingValue) {
      try {
        const hiddenMenus = JSON.parse(menuSettings.settingValue)
        if (Array.isArray(hiddenMenus)) {
          const normalizedPath = normalizePath(path)
          const isHidden = hiddenMenus.some(hiddenPath => {
            const normHidden = normalizePath(hiddenPath)
            return normalizedPath === normHidden || normalizedPath.startsWith(normHidden + '/')
          })
          if (isHidden) return false
        }
      } catch (e) {}
    }

    // First check for explicit block at user level via level check
    if (getPermissionLevel(path) === 0) return false

    if (isAdmin) return true

    // Wait for menus to be loaded before checking permissions
    if (loadingMenus || menus === undefined) {
      return false
    }

    try {
      const normalizedPath = normalizePath(path)
      const menusArray = Array.isArray(menus) ? menus : []
      
      let menu = menusArray.find(m => {
        if (!m.menuURL) return false
        const menuPath = normalizePath(m.menuURL)
        return menuPath === normalizedPath || menuPath.toLowerCase() === normalizedPath.toLowerCase()
      })
      
      if (!menu) {
        const menuName = getMenuNameFromPath(normalizedPath)
        if (menuName) {
          menu = menusArray.find(m => m.menuName === menuName || m.menuText === menuName)
        }
      }

      if (!menu) return isAdmin

      // Check action permission via API
      const response = await apiClient.get(API_ENDPOINTS.actionPermissions.check, {
        params: { menuId: menu.menuId, actionType }
      })
      
      return response.data?.allowed === true
    } catch (error) {
      // If API fails, fall back to permission level check
      const level = getPermissionLevel(path)
      const actionMap: Record<string, number> = {
        'view': 1, 'read': 1, 'create': 2, 'update': 2, 'write': 2,
        'edit-date': 2, 'edit-price': 2, 'edit-expense': 2, 'add-line': 2, 'delete-line': 2,
        'account-statement': 1, 'delete': 3, 'print': 1,
        'export': 1, 'import': 2, 'copy': 1, 'paste': 2, 'approve': 3,
        'reject': 3, 'cancel': 3,
      }
      const requiredLevel = actionMap[actionType.toLowerCase()] || 1
      return level >= requiredLevel
    }
  }, [user, menus, loadingMenus, isAdmin, menuSettings, getPermissionLevel])

  // Synchronous version using cached permissions
  const canPerformDetailedActionSync = useCallback((path: string, actionType: string): boolean => {
    if (!user) return false
    if (!path) return false

    // Step 0: Check global menu visibility settings
    if (menuSettings && menuSettings.settingValue) {
      try {
        const hiddenMenus = JSON.parse(menuSettings.settingValue)
        if (Array.isArray(hiddenMenus)) {
          const normalizedPath = normalizePath(path)
          const isHidden = hiddenMenus.some(hiddenPath => {
            const normHidden = normalizePath(hiddenPath)
            return normalizedPath === normHidden || normalizedPath.startsWith(normHidden + '/')
          })
          if (isHidden) return false
        }
      } catch (e) {}
    }

    // Check level first (handles Admin block too)
    const level = getPermissionLevel(path)
    if (level === 0) return false

    // Wait for menus to be loaded
    if (loadingMenus || menus === undefined) {
      return false
    }

    // Normalize path and action type
    const normalizedPath = normalizePath(path)
    const normalizedActionType = actionType.toLowerCase()
    const menusArray = Array.isArray(menus) ? menus : []

    // Find menu by path
    let menu = menusArray.find(m => {
      if (!m.menuURL) return false
      const menuPath = normalizePath(m.menuURL)
      return menuPath === normalizedPath || menuPath.toLowerCase() === normalizedPath.toLowerCase()
    })
    
    if (!menu) {
      const menuName = getMenuNameFromPath(normalizedPath)
      if (menuName) {
        menu = menusArray.find(m => m.menuName === menuName || m.menuText === menuName)
      }
    }

    // If menu not found, allow for admins, deny for others
    if (!menu) return isAdmin

    // Step 1: Check for explicit USER-LEVEL action block (highest priority)
    if (userActionPermissions !== undefined) {
      const userActionPermission = userActionPermissions.find(ap => {
        const menuMatch = ap.menuId === menu!.menuId
        const actionMatch = ap.actionType.toLowerCase() === normalizedActionType
        const isUserPermission = (ap.userId !== null && ap.userId !== undefined)
        return menuMatch && actionMatch && isUserPermission
      })
      
      if (userActionPermission !== undefined) {
        const active = isActive(userActionPermission.active)
        const allowed = isAllowed(userActionPermission.allowed)
        if (!active || !allowed) return false
        if (active && allowed) return true
      }
    }

    // Step 2: Admin has all permissions if not explicitly blocked
    if (isAdmin) {
      if (menuSettings && menuSettings.settingValue) {
        try {
          const hiddenMenus = JSON.parse(menuSettings.settingValue)
          if (Array.isArray(hiddenMenus)) {
            const isHidden = hiddenMenus.some(hiddenPath => pathsMatch(path, hiddenPath))
            if (isHidden) return false
          }
        } catch (e) {}
      }
      return true
    }

    // Step 3: Check Role-specific action permissions
    if (roleActionPermissions !== undefined && roleActionPermissions.length > 0) {
      const roleActionPermission = roleActionPermissions.find(ap => {
        const menuMatch = ap.menuId === menu!.menuId
        const actionMatch = ap.actionType.toLowerCase() === normalizedActionType
        const activeCheck = isActive(ap.active)
        const isRolePermission = (ap.roleId !== null && ap.roleId !== undefined)
        return menuMatch && actionMatch && activeCheck && isRolePermission
      })
      
      if (roleActionPermission !== undefined) {
        return isAllowed(roleActionPermission.allowed)
      }
    }

    // Fallback to permission level check
    const actionMap: Record<string, number> = {
        'view': 1, 'read': 1, 'create': 2, 'update': 2, 'write': 2,
        'edit-date': 2, 'edit-price': 2, 'edit-expense': 2, 'add-line': 2, 'delete-line': 2,
        'account-statement': 1, 'delete': 3, 'print': 1,
        'export': 1, 'import': 2, 'copy': 1, 'paste': 2, 'approve': 3,
        'reject': 3, 'cancel': 3, 'post': 2, 'generate': 2,
    }
    
    const requiredLevel = actionMap[normalizedActionType] || 1
    return level >= requiredLevel
  }, [user, menus, loadingMenus, userMenus, roleMenus, userActionPermissions, roleActionPermissions, isAdmin, menuSettings, getPermissionLevel])

  // Explicit action permission check (no permission-level fallback)
  const canPerformExplicitActionSync = useCallback((path: string, actionType: string): boolean => {
    if (!user) return false
    if (!path) return false
    
    const normalizedAction = actionType.toLowerCase()
    
    // Check level first
    if (getPermissionLevel(path) === 0) return false

    // Don't auto-allow admin for explicit lock/sensitive actions
    const sensitiveActions = ['edit-date', 'account-statement', 'lock-branch', 'lock-store', 'lock-customer']
    if (isAdmin && !sensitiveActions.includes(normalizedAction)) {
      if (menuSettings && menuSettings.settingValue) {
        try {
          const hiddenMenus = JSON.parse(menuSettings.settingValue)
          if (Array.isArray(hiddenMenus)) {
            const isHidden = hiddenMenus.some(hiddenPath => pathsMatch(path, hiddenPath))
            if (isHidden) return false
          }
        } catch (e) {}
      }
      return true
    }

    if (loadingMenus || menus === undefined) return false
    if (loadingUserActionPermissions || loadingRoleActionPermissions) return false

    const normalizedPath = normalizePath(path)
    const menusArray = Array.isArray(menus) ? menus : []

    let menu = menusArray.find(m => {
      if (!m.menuURL) return false
      const menuPath = normalizePath(m.menuURL)
      return menuPath === normalizedPath || menuPath.toLowerCase() === normalizedPath.toLowerCase()
    })

    if (!menu) {
      const menuName = getMenuNameFromPath(normalizedPath)
      if (menuName) {
        menu = menusArray.find(m => m.menuName === menuName || m.menuText === menuName)
      }
    }

    if (!menu) return isAdmin && !sensitiveActions.includes(normalizedAction)

    if (userActionPermissions !== undefined) {
      const userActionPermission = userActionPermissions.find(ap => {
        const menuMatch = ap.menuId === menu!.menuId
        const actionMatch = ap.actionType.toLowerCase() === normalizedAction
        const activeCheck = isActive(ap.active)
        const isUserPermission = (ap.userId !== null && ap.userId !== undefined)
        return menuMatch && actionMatch && activeCheck && isUserPermission
      })

      if (userActionPermission !== undefined) {
        return isAllowed(userActionPermission.allowed)
      }
    }

    if (roleActionPermissions !== undefined && roleActionPermissions.length > 0) {
      const roleActionPermission = roleActionPermissions.find(ap => {
        const menuMatch = ap.menuId === menu!.menuId
        const actionMatch = ap.actionType.toLowerCase() === normalizedAction
        const activeCheck = isActive(ap.active)
        const isRolePermission = (ap.roleId !== null && ap.roleId !== undefined)
        return menuMatch && actionMatch && activeCheck && isRolePermission
      })

      if (roleActionPermission !== undefined) {
        return isAllowed(roleActionPermission.allowed)
      }
    }

    return false
  }, [user, menus, loadingMenus, userActionPermissions, roleActionPermissions, isAdmin, getPermissionLevel])

  // Only block navigation while core menus are still loading.
  // Admins must not wait on role/user/action permission lists — those queries can hang
  // and were blocking every ProtectedRoute (including /permissions, /roles).
  const isLoading = (!!user && loadingMenus && menus === undefined)

  const isPermissionsLoading = isAdmin
    ? isLoading
    : (
        isLoading ||
        (!!user && loadingUserMenus && userMenus === undefined) ||
        (!!userRole && loadingRoleMenus && roleMenus === undefined) ||
        (!!user && loadingUserActionPermissions && userActionPermissions === undefined) ||
        (!!userRole && loadingRoleActionPermissions && roleActionPermissions === undefined)
      )

  return {
    menus: menus || [],
    userMenus: userMenus || [],
    roleMenus: roleMenus || [],
    userActionPermissions: userActionPermissions || [],
    roleActionPermissions: roleActionPermissions || [],
    hasPermission,
    getPermissionLevel,
    canPerformAction,
    canPerformDetailedAction,
    canPerformDetailedActionSync,
    canPerformExplicitActionSync,
    isLoading: isLoading,
    isPermissionsLoading: isPermissionsLoading,
    isAdmin,
  }
}
